import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "./Style.css";

export default function TournamentScreen() {
  const { cardroom, id } = useParams();
  const token = localStorage.getItem("token");
  
  // Data
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Local Timer State
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [payoutRotationIdx, setPayoutRotationIdx] = useState(0);
  
  // Optimistic Level State (to handle 0:00 transition smoothly)
  const [optimisticLevelIdx, setOptimisticLevelIdx] = useState(null);

  // Refs for consistent timer loop access and auto-advance logic
  const tournamentRef = useRef(null);
  const optimisticLevelRef = useRef(null);
  const virtualStartRef = useRef(null);
  const virtualDurationRef = useRef(0);

  // Sync refs & Handle Server Updates
  useEffect(() => {
    tournamentRef.current = tournament;
    if (tournament) {
        const serverLevel = Number(tournament.current_level || 0);
        const serverIdx = serverLevel === 0 ? -1 : serverLevel - 1;
        
        // Init if first load
        if (optimisticLevelIdx === null) {
            setOptimisticLevelIdx(serverIdx);
            optimisticLevelRef.current = serverIdx;
        } else {
            // Check for server sync
            // If server matches or exceeds our optimistic view, trust server
            // This handles manual updates or server catching up
            if (serverIdx >= optimisticLevelRef.current) {
                if (serverIdx !== optimisticLevelRef.current) {
                    setOptimisticLevelIdx(serverIdx);
                    optimisticLevelRef.current = serverIdx;
                }
                // Clear virtual mode as server is now the authority
                virtualStartRef.current = null;
                virtualDurationRef.current = 0;
            }
        }
    }
  }, [tournament, optimisticLevelIdx]);

  // Set auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  // Poll Data
  useEffect(() => {
    if (!token) return;
    loadData();
    const interval = setInterval(loadData, 5000); 
    return () => clearInterval(interval);
  }, [id, cardroom, token]);

  // Optimized Timer Logic (Runs independently of React render cycles)
  useEffect(() => {
    const tick = () => {
        const t = tournamentRef.current;
        if (!t) return;
        
        const now = Date.now();
        
        // Determine Current Level (Optimistic or Server)
        let currentIdx = optimisticLevelRef.current;
        if (currentIdx === null) {
            const serverLevel = Number(t.current_level || 0);
            currentIdx = serverLevel === 0 ? -1 : serverLevel - 1;
        }

        let remaining = 0;
        const isRunning = t.state === 'running';

        if (virtualStartRef.current && isRunning) {
             // Virtual Mode (Auto-advanced locally)
             const elapsed = (now - virtualStartRef.current) / 1000;
             remaining = Math.max(0, virtualDurationRef.current - elapsed);
        } else if (isRunning && t.timer_start_at) {
             // Server Mode
             const startAt = new Date(t.timer_start_at).getTime();
             const diff = (now - startAt) / 1000;
             remaining = Math.max(0, t.timer_remaining_seconds - diff);
        } else {
             // Paused / Stopped
             remaining = t.timer_remaining_seconds || 0;
        }

        // Auto-Advance Check
        if (isRunning && remaining <= 0) {
             const config = t.config_json || {};
             const levels = config.levels || [];
             
             // If we have a next level
             if (currentIdx < levels.length - 1) {
                  const nextIdx = currentIdx + 1;
                  const nextLevel = levels[nextIdx];
                  const nextDuration = (nextLevel.duration || 15) * 60;

                  // Advance Optimistically and Start Virtual Timer
                  setOptimisticLevelIdx(nextIdx);
                  optimisticLevelRef.current = nextIdx;
                  
                  // Set Virtual Start to NOW
                  virtualStartRef.current = now;
                  virtualDurationRef.current = nextDuration;
                  
                  remaining = nextDuration; // Display full time immediately
             }
        }
        
        setTimeLeft(remaining);

        // Update Current Time
        setCurrentTime(Date.now());
    };

    tick(); 
    const interval = setInterval(tick, 200); 
    return () => clearInterval(interval);
  }, []); // Empty dependency array = stable interval. Refs handle data access.

  // Ref for places paid (needed before early return for rotation effect)
  const placesPaidRef = useRef(1);

  // Rotation for footer display (must be before early return)
  useEffect(() => {
      const pp = placesPaidRef.current;
      if (pp <= 5) return;
      const rotateInterval = setInterval(() => {
          setPayoutRotationIdx(prev => (prev + 5) % Math.max(1, pp));
      }, 4000);
      return () => clearInterval(rotateInterval);
  }, []);

  // Update placesPaidRef when tournament data changes
  useEffect(() => {
      if (!tournament) return;
      const config = tournament.config_json || {};
      const payoutMode = config.payoutMode || 'percent';
      const payoutValue = config.payoutValue || 15;
      let pp = 0;
      if (payoutMode === 'custom') {
          pp = Array.isArray(config.customPayouts) ? config.customPayouts.length : 0;
      } else if (payoutMode === 'fixed') {
          pp = payoutValue;
      } else {
          pp = Math.floor(tournament.total_entries * (payoutValue / 100));
      }
      if (pp < 1 && tournament.total_entries > 0) pp = 1;
      placesPaidRef.current = pp;
  }, [tournament]);

  const loadData = async () => {
    try {
        const { data } = await axios.get(`/${cardroom}/api/tournaments/${id}`);
        setTournament(data);
    } catch (err) {
        console.error("Load error", err);
    } finally {
        setLoading(false);
    }
  };

  if (loading || !tournament) return <div style={{background: '#000', color: '#fff', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>Loading...</div>;

  // DERIVED DATA
  const config = tournament.config_json || {};
  const displaySettings = config.display_settings || {
      playersLeft: true,
      avgStack: true,
      entries: true,
      buyinsNum: true,
      rebuysNum: true,
      addonsNum: true,
      bonusNum: true,
      totalChips: true,
      totalPot: true,
      placesPaid: true,
      prizes: true
  };
  const levels = config.levels || [];
  const mixedGames = Boolean(config.mixedGames);
  
  const serverLevelNumber = Number(tournament.current_level || 0);
  const serverLevelIdx = serverLevelNumber === 0 ? -1 : serverLevelNumber - 1;
  const safeLvlIdx = optimisticLevelIdx !== null ? optimisticLevelIdx : serverLevelIdx;
  const isCountdownLevel = safeLvlIdx < 0;
  const currentLevelData = isCountdownLevel ? {} : (levels[safeLvlIdx] || {});
  
  // Find next break and calculate time until it starts
  let nextBreakIdx = -1;
  for (let i = Math.max(safeLvlIdx, -1) + 1; i < levels.length; i++) {
      if (levels[i].isBreak) {
          nextBreakIdx = i;
          break;
      }
  }

  let nextBreakCountdownSeconds = null;
  if (nextBreakIdx >= 0) {
      nextBreakCountdownSeconds = Math.max(0, timeLeft || 0);
      const firstFullLevelIdx = Math.max(safeLvlIdx + 1, 0);
      for (let i = firstFullLevelIdx; i < nextBreakIdx; i++) {
          nextBreakCountdownSeconds += (levels[i]?.duration || 15) * 60;
      }
  }

  const formatTime = (s) => {
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const formatDuration = (s) => {
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = Math.floor(s % 60);
      return `${h}:${m < 10 ? '0' : ''}${m}:${sec < 10 ? '0' : ''}${sec}`;
  };
  
  const formatBlind = (val) => {
      if (!val) return 0;
      if (val >= 1000) {
          // Check if whole thousand
          if (val % 1000 === 0) return (val / 1000) + 'k';
          return (val / 1000).toFixed(1) + 'k';
      }
      return val;
  };

  const formatGameType = (level) =>
      mixedGames && level?.gameType ? ` ${level.gameType}` : "";

  const formatLevelBlinds = (level) => {
      if (!level) return "Finished";
      if (level.isBreak) return "BREAK";
      const anteText = level.ante > 0 ? ` / ${formatBlind(level.ante)}` : "";
      return `${formatBlind(level.sb)} / ${formatBlind(level.bb)}${anteText}${formatGameType(level)}`;
  };

  const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  const numFormatter = new Intl.NumberFormat('en-US');

  // SAFE ACCESSORS
  const totalPot = tournament.prizepool || 0; 
  const totalChips = tournament.total_chips || 0;
  const avgStack = tournament.players_left > 0 ? Math.floor(totalChips / tournament.players_left) : 0;


  // STYLES
  const gridStyle = {
      display: 'grid',
      gridTemplateColumns: '20% 60% 20%', // Adjusted ratio per user request (was 25/50/25)
      gridTemplateRows: 'auto 1fr auto',
      height: '100vh',
      width: '100vw',
      background: '#002b5c', // Deep Blue from image
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      overflow: 'hidden'
  };

  const borderStyle = "2px solid #fff";
  const boxStyle = {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderBottom: borderStyle,
      padding: '0.5rem',
      overflow: 'hidden'
  };
  
  const labelStyle = { fontSize: '1.2vw', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '0.2rem', textAlign: 'center' };
  const valueStyle = { fontSize: '2.5vw', fontWeight: 'bold' };

  // Payout Calculation using WSOP Blueprint Matrices
  const calculatePayouts = (prizepool, numPlaces, exponent = 1.5, buyIn = 100, minCashPercent = 150) => {
      if (numPlaces <= 0 || prizepool <= 0) return [];
      
      const minCash = buyIn * (minCashPercent / 100);
      let totalMinCash = minCash * numPlaces;
      
      let actualMinCash = minCash;
      if (totalMinCash > prizepool) {
          actualMinCash = prizepool / numPlaces; 
          totalMinCash = prizepool;
      }


      // ── Shifted Inverse Power Law (Zipf with shift) ──────────────────────
      // w_i = 1 / (i + c)^k, fitted from WSOP dataset:
      //   exp 1.0 → Flatter (k=2.50, c=6.5)
      //   exp 1.2 → Flat    (k=2.50, c=5.0)
      //   exp 1.5 → Normal  (k=2.50, c=4.5)
      //   exp 2.0 → Steep   (k=2.41, c=3.0)
      const PRESETS = [
          { exp: 1.0, k: 2.50, c: 6.5 },
          { exp: 1.2, k: 2.50, c: 5.0 },
          { exp: 1.5, k: 2.50, c: 4.5 },
          { exp: 2.0, k: 2.41, c: 3.0 },
      ];

      let left = PRESETS[0], right = PRESETS[PRESETS.length - 1];
      for (let i = 0; i < PRESETS.length - 1; i++) {
          if (exponent >= PRESETS[i].exp && exponent <= PRESETS[i + 1].exp) {
              left = PRESETS[i]; right = PRESETS[i + 1]; break;
          }
      }
      const t = right.exp === left.exp ? 0 : (exponent - left.exp) / (right.exp - left.exp);
      const k = left.k + t * (right.k - left.k);
      const c = left.c + t * (right.c - left.c);

      const weights = [];
      let totalWeight = 0;
      for (let i = 1; i <= numPlaces; i++) {
          const w = 1 / Math.pow(i + c, k);
          weights.push(w);
          totalWeight += w;
      }
      if (totalWeight <= 0) totalWeight = 1;

      const remainingPool = prizepool - actualMinCash * numPlaces;

      let payouts = weights.map((w, i) => ({
          place: i + 1,
          amount: actualMinCash + (w / totalWeight) * remainingPool
      }));

      const tiers = [
          { start: 10, end: 27,       step: 3  },
          { start: 28, end: 99,       step: 9  },
          { start: 100, end: Infinity, step: 10 },
      ];
      for (const tier of tiers) {
          if (numPlaces < tier.start) continue;
          const tierEnd = Math.min(numPlaces, tier.end);
          for (let i = tier.start; i <= tierEnd; i += tier.step) {
              const groupEnd = Math.min(i + tier.step - 1, tierEnd);
              if (groupEnd > i) {
                  let sum = 0;
                  for (let j = i; j <= groupEnd; j++) sum += payouts[j - 1].amount;
                  const avg = sum / (groupEnd - i + 1);
                  for (let j = i; j <= groupEnd; j++) payouts[j - 1].amount = avg;
              }
          }
      }

      let totalAssigned = 0;
      for (let i = 0; i < payouts.length; i++) {
          payouts[i].amount = Math.round(payouts[i].amount);
          totalAssigned += payouts[i].amount;
      }
      if (payouts.length > 0) payouts[0].amount += prizepool - totalAssigned;

      return payouts;
  };

  // Ordinal suffix helper
  const getOrdinal = (n) => {
      if (n === 1) return 'st';
      if (n === 2) return 'nd';
      if (n === 3) return 'rd';
      return 'th';
  };

  // Payout / Bubble Logic - Read from config_json
  const payoutMode = config.payoutMode || 'percent'; // 'fixed', 'percent', or 'custom'
  const payoutValue = config.payoutValue || 15; // number or percentage
  const payoutExponent = config.payoutExponent || 1.5;
  const minCashPercent = config.minCashPercent || 150;
  const hasCustomPayouts = Array.isArray(config.customPayouts) && config.customPayouts.length > 0;
  
  let placesPaid = 0;
  if (hasCustomPayouts) {
      placesPaid = config.customPayouts.length;
  } else if (payoutMode === 'fixed') {
      placesPaid = payoutValue;
  } else {
      // Percent of entries - use FLOOR per user requirement
      placesPaid = Math.floor(tournament.total_entries * (payoutValue / 100));
  }
  if (placesPaid < 1 && tournament.total_entries > 0) placesPaid = 1;
  
  const hasEditedAmountPayouts = Array.isArray(config.customPayouts)
      && config.customPayouts.length > 0
      && config.customPayouts.some(p => p.amount !== undefined);
  const percentageCustomPayouts = Array.isArray(config.customPayouts)
      ? config.customPayouts.map((p, idx) => {
          const percentage = p.percentage !== undefined
              ? Number(p.percentage) || 0
              : (totalPot > 0 ? ((Number(p.amount) || 0) / totalPot) * 100 : 0);
          const place = Number(p.place) || idx + 1;
          return {
              place,
              amount: p.amount !== undefined ? Number(p.amount) || 0 : Math.round(totalPot * (percentage / 100))
          };
      })
      : [];
  if (percentageCustomPayouts.length > 0) {
      const assigned = percentageCustomPayouts.reduce((sum, p) => sum + p.amount, 0);
      percentageCustomPayouts[0].amount += totalPot - assigned;
  }
  const rawPayouts = percentageCustomPayouts.length > 0
      ? percentageCustomPayouts
      : (hasEditedAmountPayouts ? config.customPayouts : calculatePayouts(totalPot, placesPaid, payoutExponent, tournament.buyin || 100, minCashPercent));
  
  // Condense for display
  let payouts = [];
  for(let i=0; i<rawPayouts.length; i++) {
      let p = rawPayouts[i];
      let startPlace = p.place;
      let endPlace = p.place;
      let groupAmount = p.amount;
      while(i + 1 < rawPayouts.length && rawPayouts[i+1].amount === groupAmount) {
          endPlace = rawPayouts[i+1].place;
          i++;
      }
      
      let displayPlace = "";
      if (startPlace === endPlace) {
          displayPlace = `${startPlace}${getOrdinal(startPlace)}`;
      } else {
          displayPlace = `${startPlace}${getOrdinal(startPlace)}-${endPlace}${getOrdinal(endPlace)}`;
      }
      payouts.push({ displayPlace, amount: groupAmount, key: startPlace });
  }

  const sortedRawPayouts = [...rawPayouts].sort((a, b) => Number(a.place) - Number(b.place));
  const highestPaidPlace = sortedRawPayouts.reduce((max, p) => Math.max(max, Number(p.place) || 0), 0);
  const playersLeft = Math.max(0, Number(tournament.players_left) || 0);
  const nextPrizePlace = highestPaidPlace > 0 && playersLeft > 0
      ? Math.min(playersLeft, highestPaidPlace)
      : null;
  const nextPrizePayout = nextPrizePlace === null
      ? null
      : sortedRawPayouts.find(p => Number(p.place) === nextPrizePlace);
  const nextPrizeAmount = nextPrizePayout ? Number(nextPrizePayout.amount) || 0 : null;

  // Rotation constants
  const maxDisplayed = 5;
  const displayItemsCount = payouts.length;
  const needsRotation = displayItemsCount > maxDisplayed;
  
  // Check if timer string is long (e.g. HH:MM:SS) to adjust font size
  const timerStr = formatTime(timeLeft);
  const timerFontSize = timerStr.length > 5 ? '15vw' : '20vw';

  // Next Level Info
  const nextLvlIdx = safeLvlIdx + 1;
  const nextLevelData = levels[nextLvlIdx] || null;
  const nextLevelText = formatLevelBlinds(nextLevelData);

  return (
    <div style={gridStyle}>
        
        {/* HEADER */}
        <div style={{gridColumn: '1 / 4', background: '#002b5c', padding: '1rem', textAlign: 'center', borderBottom: borderStyle}}>
             <div style={{fontSize: '3vw', fontWeight: 'bold'}}>{tournament.name}</div>
             <div style={{fontSize: '1vw', opacity: 0.8}}>
                ${tournament.buyin} Buy-in &bull; ${tournament.rebuys_cost} Re-buy &bull; Rebuys Through Level {tournament.rebuys_cutoff_level}
             </div>
        </div>

        {/* EXPOSE DISPLAY SETTINGS */}
        {/* hoisted so footer can also reference displaySettings */}
        {(() => {
            return (
                <>
                   {/* LEFT COLUMN */}
                   <div style={{display: 'flex', flexDirection: 'column', borderRight: borderStyle}}>
                        <div style={{...boxStyle, flex: 1}}>
                            <div style={labelStyle}>LEVEL</div>
                            <div style={valueStyle}>{isCountdownLevel ? "Countdown" : safeLvlIdx + 1}</div>
                        </div>
                        {displaySettings.entries !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Total Entries</div>
                                <div style={valueStyle}>{tournament.total_entries}</div>
                            </div>
                        )}
                        {displaySettings.playersLeft !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Players Left</div>
                                <div style={valueStyle}>
                                    {tournament.players_left
                                        ? `${tournament.players_left} / ${tournament.total_entries}`
                                        : tournament.total_entries}
                                    </div>
                            </div>
                        )}
                        {displaySettings.buyinsNum !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>{displaySettings.entries === false ? "Entries" : "Unique Entries"}</div>
                                <div style={valueStyle}>{tournament.buyins_num || 0}</div>
                            </div>
                        )}
                        {displaySettings.rebuysNum !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Rebuys</div>
                                <div style={valueStyle}>{tournament.rebuys_num || 0}</div>
                            </div>
                        )}
                        {displaySettings.addonsNum !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Add Ons</div>
                                <div style={valueStyle}>{tournament.addons_num || 0}</div>
                            </div>
                        )}
                        {displaySettings.placesPaid !== false && (
                            <div style={{...boxStyle, flex: 1, borderBottom: 'none'}}>
                                <div style={labelStyle}>Bubble</div>
                                <div style={valueStyle}>{placesPaid}</div>
                            </div>
                        )}
                   </div>

                   {/* CENTER COLUMN */}
{/* CENTER COLUMN */}
<div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
    {/* Inline formatting utility to bypass 'k' shortening and force full comma-separated values */}
    {(() => {
        const formatWithCommas = (val) => {
            if (val === undefined || val === null) return '';
            const num = Number(val);
            return isNaN(num) ? val : num.toLocaleString();
        };

        return (
            <>
                {/* TIMER */}
                <div style={{ flex: 3.5, display: 'flex', justifyContent: 'center', alignItems: 'center', borderBottom: borderStyle }}>
                     <div style={{
                         fontSize: `calc(${timerFontSize} * 0.85)`, 
                         fontWeight: 'bold', 
                         lineHeight: 1, 
                         fontVariantNumeric: 'tabular-nums',
                         color: timeLeft < 60 && tournament.state === 'running' ? '#ff4d4d' : '#fff',
                         transition: 'font-size 0.2s ease'
                     }}>
                         {timerStr}
                     </div>
                </div>

                {/* CURRENT LEVEL */}
                <div style={{ flex: 5, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '1rem', overflow: 'hidden', borderBottom: borderStyle }}>
                    {/* Brightened and tracked out to stand out more */}
                    <div style={{ fontSize: '1.2vw', fontWeight: '700', opacity: 0.9, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '0.4vw' }}>
                        Current Level
                    </div>

                    {isCountdownLevel ? (
                        <div style={{ fontSize: '5.5vw', fontWeight: 'bold' }}>COUNTDOWN</div>
                    ) : currentLevelData?.isBreak ? (
                        <div style={{ fontSize: '6.5vw', fontWeight: 'bold' }}>BREAK</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                            {/* Game Type - High visibility row */}
                            {formatGameType(currentLevelData) && (
                                <div style={{ fontSize: '2.4vw', fontWeight: '800', color: '#fff', marginBottom: '0.6vw', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    {formatGameType(currentLevelData).trim()}
                                </div>
                            )}
                            
                            {/* Blinds Row */}
                            <div style={{
                                fontSize: '5.5vw', // Adjusted down slightly to perfectly clear large numbers like 100,000 / 200,000
                                fontWeight: 'bold', 
                                whiteSpace: 'nowrap',
                                lineHeight: 1.1,
                                display: 'flex',
                                alignItems: 'center'
                            }}>                               
                               {formatWithCommas(currentLevelData?.sb)} / {formatWithCommas(currentLevelData?.bb)}
                               <span style={{ fontSize: '1.5vw', fontWeight: '600', color: '#aaa', marginLeft: '1.5vw', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Blinds</span>
                            </div>

                            {/* Ante Row - Stacked directly below blinds */}
                            {currentLevelData?.ante > 0 && (
                                <div style={{ 
                                    fontSize: '3.8vw', 
                                    fontWeight: '700', 
                                    color: '#ddd', 
                                    marginTop: '0.4vw',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {formatWithCommas(currentLevelData.ante)}
                                    <span style={{ fontSize: '1.4vw', fontWeight: '600', color: '#999', marginLeft: '1.2vw', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ante</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* NEXT LEVEL INFO */}
                <div style={{ flex: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0.5rem', overflow: 'hidden' }}>
                    <div style={{ fontSize: '1.1vw', opacity: 0.6, color: '#ccc', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.3vw' }}>
                        Next Level
                    </div>
                    
                    {typeof nextLevelData !== 'undefined' && nextLevelData ? (
                        nextLevelData.isBreak ? (
                            <div style={{ fontSize: '2.8vw', fontWeight: 'bold', color: '#aaa' }}>BREAK</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* Next Game Type */}
                                {formatGameType(nextLevelData) && (
                                    <div style={{ fontSize: '1.3vw', fontWeight: '700', color: '#aaa', marginBottom: '0.2vw', textTransform: 'uppercase' }}>
                                        {formatGameType(nextLevelData).trim()}
                                    </div>
                                )}

                                {/* Next Blinds Row */}
                                <div style={{
                                    fontSize: '2.8vw', 
                                    fontWeight: 'bold', 
                                    whiteSpace: 'nowrap',
                                    lineHeight: 1.1,
                                    color: '#ddd',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {formatWithCommas(nextLevelData.sb)} / {formatWithCommas(nextLevelData.bb)}
                                    <span style={{ fontSize: '1vw', fontWeight: '600', color: '#888', marginLeft: '0.8vw', textTransform: 'uppercase' }}>Blinds</span>
                                </div>

                                {/* Next Ante Row */}
                                {nextLevelData.ante > 0 && (
                                    <div style={{ 
                                        fontSize: '2vw', 
                                        fontWeight: '700', 
                                        color: '#bbb', 
                                        marginTop: '0.2vw',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>                                        
                                        {formatWithCommas(nextLevelData.ante)}
                                        <span style={{ fontSize: '0.9vw', fontWeight: '600', color: '#777', marginLeft: '0.6vw', textTransform: 'uppercase' }}>Ante</span>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <div style={{ fontSize: '2.5vw', fontWeight: 'bold', color: '#bbb' }}>
                            {nextLevelText}
                        </div>
                    )}
                </div>
            </>
        );
    })()}
</div>

                   {/* RIGHT COLUMN */}
                   <div style={{display: 'flex', flexDirection: 'column', borderLeft: borderStyle}}>
                        <div style={{...boxStyle, flex: 1}}>
                            <div style={labelStyle}>Current Time</div>
                            <div style={valueStyle}>{new Date(currentTime).toLocaleTimeString([], {hour: 'numeric', minute:'2-digit', second: '2-digit'})}</div>
                        </div>
                        <div style={{...boxStyle, flex: 1}}>
                            <div style={labelStyle}>Next Break</div>
                            <div style={valueStyle}>
                                {nextBreakCountdownSeconds === null ? "None" : formatDuration(nextBreakCountdownSeconds)}
                            </div>
                        </div>
                        {displaySettings.prizes !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Next Prize</div>
                                <div style={valueStyle}>
                                    {nextPrizeAmount === null ? "None" : moneyFormatter.format(nextPrizeAmount)}
                                </div>
                            </div>
                        )}
                        {displaySettings.totalChips !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Total Chips</div>
                                <div style={valueStyle}>{numFormatter.format(tournament.total_chips || 0)}</div>
                            </div>
                        )}
                        {displaySettings.avgStack !== false && (
                            <div style={{...boxStyle, flex: 1}}>
                                <div style={labelStyle}>Avg Stack</div>
                                <div style={valueStyle}>{numFormatter.format(avgStack)}</div>
                            </div>
                        )}
                        {displaySettings.totalPot !== false && (
                            <div style={{...boxStyle, flex: 1, borderBottom: 'none'}}>
                                <div style={labelStyle}>Total Pot</div>
                                <div style={valueStyle}>{moneyFormatter.format(totalPot)}</div>
                            </div>
                        )}
                   </div>
                </>
            );
        })()}

        {displaySettings.prizes !== false && (
        <div style={{gridColumn: '1 / 4', background: '#001f42', padding: '0.8rem', borderTop: borderStyle, overflow: 'hidden', position: 'relative'}}>
             {needsRotation ? (
                 <div style={{
                     display: 'flex',
                     animation: `marquee ${displayItemsCount * 3}s linear infinite`,
                     width: 'fit-content'
                 }}>
                     {/* First copy of payouts - each copy fills at least 100vw */}
                     <div style={{display: 'flex', minWidth: '100vw', justifyContent: 'space-around'}}>
                         {payouts.map((p, idx) => (
                             <div key={`a-${p.key}`} style={{fontSize: '1.5vw', fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                                 {p.displayPlace}: {moneyFormatter.format(p.amount)}
                             </div>
                         ))}
                     </div>
                     {/* Second copy for seamless loop */}
                     <div style={{display: 'flex', minWidth: '100vw', justifyContent: 'space-around'}}>
                         {payouts.map((p, idx) => (
                             <div key={`b-${p.key}`} style={{fontSize: '1.5vw', fontWeight: 'bold', whiteSpace: 'nowrap'}}>
                                 {p.displayPlace}: {moneyFormatter.format(p.amount)}
                             </div>
                         ))}
                     </div>
                 </div>
             ) : (
                 <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'center'}}>
                     {payouts.slice(0, maxDisplayed).map(p => (
                         <div key={p.key} style={{fontSize: '1.5vw', fontWeight: 'bold'}}>
                             {p.displayPlace}: {moneyFormatter.format(p.amount)}
                         </div>
                     ))}
                 </div>
             )}
             <style>{`
                 @keyframes marquee {
                     0% { transform: translateX(0); }
                     100% { transform: translateX(-50%); }
                 }
             `}</style>
        </div>
        )}

    </div>
  );
}
